/**
 * The execution pack — one run's approved lines to execute by hand in Oracle (bata-platform
 * ADR 6037, owner ruling R2, `docs/adr/6004`). Pure: the flattening, the tallies and the two
 * CSV sheets are asserted on rows rather than read off a render.
 *
 * **The pack is the beta's only outbound channel.** There is no ERP integration (owner ruling
 * 2026-09-17, R2 for the beta): an approval mints a write-back command in state `approved`, a
 * planner exports the pack and reports `dispatched`, executes the moves in Oracle and reports
 * `acknowledged`. Nothing else moves a command, and nothing here claims more than those two
 * reports say — `dispatched` is "exported, awaiting confirmation", never "sent" or "done"
 * (§14 test E10).
 *
 * **One line, one command, with its authority.** The platform groups the lines as a person
 * executes them (origin → store, donor → recipient, article × week); the CSV keeps the grouping
 * as two columns and carries each line's provenance — run, decision, actor, time, reason — beside
 * it, because an instruction nobody can trace to a decision is one nobody should execute. The
 * proposal origin is published ABSENT (`origin_not_recorded`: a proposal stores its door and not
 * the distribution centre behind it) and leaves the file as `not-reported`, never as a guess.
 */

import type {
  ExitLanePackGroup, PackProvenance, PricePackGroup, ProposalPackGroup, TransferPackGroup,
} from '@/api/generated';
import { NOT_PUBLISHED, NO_PRINCIPAL, buildVersion, provenanceRows } from '@/export';
import type { ExportCell, ExportColumn, ExportRequest, Provenance } from '@/export';
import { PERSON_NAME_DISPLAY, SIZE_EXPORT_COLUMNS, personExportCell, sizeExportCells, sizeName, type SizeName } from '@/planning';

import type { Named, RunNames } from './run-names';

/** What a line instructs. The platform's `subject_kind` spelling, so the file joins to the log. */
export type PackSubject = 'proposal' | 'transfer' | 'price_action' | 'exit_consolidation_line';

/**
 * Anything a pack is read from: one run's (`ExecutionPack`) or a market's across its runs
 * (`MarketExecutionPack`, bata-platform ADR 6104), which also carries the exit lane.
 */
export interface PackSource {
  readonly proposals: readonly ProposalPackGroup[];
  readonly transfers: readonly TransferPackGroup[];
  readonly price_steps: readonly PricePackGroup[];
  readonly exit_lanes?: readonly ExitLanePackGroup[];
}

/** The perimeter a pack was read over — a run, or a market — and the state it was narrowed to. */
export interface PackPerimeter {
  readonly run_id?: string;
  readonly country_key: string;
  readonly state: string | null;
}

/**
 * Why a proposal group names no origin (bata-platform ADR 6106): several supply nodes held the
 * cell and the pool chose none; none held measured stock; or the line predates the record.
 */
const ORIGIN_ABSENT_KEY: Readonly<Record<string, string>> = {
  'planning.inseason.execution_pack.origin_not_recorded': 'inseason.executionPack.originNotRecorded',
  'planning.inseason.execution_pack.origin_ambiguous': 'inseason.executionPack.originAmbiguous',
  'planning.inseason.execution_pack.origin_no_supply_node': 'inseason.executionPack.originNoSupplyNode',
};

/** The label key for an absent origin's reason; an unrecognised reason reads as "not recorded". */
export function originAbsentKey(reason: string | null): string {
  return (reason === null ? undefined : ORIGIN_ABSENT_KEY[reason]) ?? 'inseason.executionPack.originNotRecorded';
}

export interface PackLine {
  readonly subject: PackSubject;
  readonly subjectId: string;
  readonly commandId: string;
  /** The command's state, verbatim — states are data (G5). */
  readonly state: string;
  readonly stateObservedAt: string;
  /** Origin → store, donor → recipient, article → effective week. `null` where unpublished. */
  readonly from: string | null;
  readonly fromAbsentReason: string | null;
  readonly to: string;
  readonly articleKey: string;
  /**
   * The option an exit-lane line moves; `null` on every other line. An option is not an article,
   * so an exit lane's `articleKey` stays the option for the screens' row header (they have no other
   * key to show) and the FILE says it in its own column, leaving `article_key` blank on that lane.
   */
  readonly optionId: string | null;
  /**
   * The size, as the platform named it beside its opaque key (`docs/adr/9027`); `null` on a
   * price step, which has no size.
   */
  readonly size: SizeName | null;
  /** The proposed units: a proposal's, a transfer's. `null` on a price step. What the decision
   *  authorises is `authorisedUnits` — the adjusted quantity on an adjust (ADR 6052 for a transfer). */
  readonly units: number | null;
  readonly authorisedUnits: number | null;
  readonly adjustedUnits: number | null;
  readonly fromPrice: number | null;
  readonly toPrice: number | null;
  /**
   * A price step rounded on the shelf (bata-reqs issue 38 R4, platform ADR 6190): the IVA-inclusive
   * price to set, the ratified rung's share off the original and the share the shelf price takes.
   * `null` on every other line and on a step priced with no rounding mode.
   */
  readonly shelfPrice: number | null;
  readonly targetDepth: number | null;
  readonly realisedDepth: number | null;
  readonly netBenefit: number | null;
  readonly currencyCode: string | null;
  /** When the approval raised the command — the backlog's age (ADR 6105). `null` from an older platform. */
  readonly createdAt: string | null;
  /** Units confirmed executed so far, cumulative (ADR 6105); `null` while none is, or on a price step. */
  readonly executedUnits: number | null;
  /** Units still to execute (ADR 6105); `null` once cancelled, on a price step, or from an older platform. */
  readonly remainingUnits: number | null;
  /**
   * The ship-week an exit lane is released in under the units-sent cap — the Monday of its ISO week
   * (bata-reqs ADR 9000 open question 1, platform #891). `null` on an exit lane released under no cap
   * (it ships as before) and on every other line, which has no tranche.
   */
  readonly trancheWeek: string | null;
  readonly provenance: PackProvenance;
}

/** Every line of the pack, in the platform's group order. */
export function packLines(pack: PackSource): readonly PackLine[] {
  const lines: PackLine[] = [];
  for (const group of pack.proposals) {
    for (const line of group.lines) {
      lines.push({
        subject: 'proposal', subjectId: line.proposal_id, commandId: line.command_id,
        state: line.state, stateObservedAt: line.state_observed_at,
        from: group.origin_node_key, fromAbsentReason: group.origin_absent_reason, to: group.store_key,
        articleKey: line.article_key, optionId: null, size: sizeName(line),
        units: line.proposed_units, authorisedUnits: line.authorised_units, adjustedUnits: line.adjusted_units,
        fromPrice: null, toPrice: null, shelfPrice: null, targetDepth: null, realisedDepth: null, netBenefit: null, currencyCode: null,
        createdAt: line.created_at ?? null, executedUnits: line.executed_units ?? null,
        remainingUnits: line.remaining_units ?? null, trancheWeek: null,
        provenance: line.provenance,
      });
    }
  }
  for (const group of pack.transfers) {
    for (const line of group.lines) {
      lines.push({
        subject: 'transfer', subjectId: line.transfer_id, commandId: line.command_id,
        state: line.state, stateObservedAt: line.state_observed_at,
        from: group.donor_store_key, fromAbsentReason: null, to: group.recipient_store_key,
        articleKey: line.article_key, optionId: null, size: sizeName(line),
        // An adjusted transfer authorises the planner's quantity (bata-platform ADR 6052); a
        // platform before it publishes neither, which reads as not stated, never as 0.
        units: line.units, authorisedUnits: line.authorised_units ?? null, adjustedUnits: line.adjusted_units ?? null,
        fromPrice: null, toPrice: null, shelfPrice: null, targetDepth: null, realisedDepth: null, netBenefit: line.net_benefit, currencyCode: line.currency_code,
        createdAt: line.created_at ?? null, executedUnits: line.executed_units ?? null,
        remainingUnits: line.remaining_units ?? null, trancheWeek: null,
        provenance: line.provenance,
      });
    }
  }
  for (const group of pack.price_steps) {
    for (const line of group.lines) {
      lines.push({
        subject: 'price_action', subjectId: line.price_action_id, commandId: line.command_id,
        state: line.state, stateObservedAt: line.state_observed_at,
        from: group.article_key, fromAbsentReason: null, to: group.effective_week,
        articleKey: group.article_key, optionId: null, size: null,
        units: null, authorisedUnits: null, adjustedUnits: null,
        fromPrice: line.from_price, toPrice: line.to_price,
        shelfPrice: line.rounding?.shelf_price ?? null, targetDepth: line.rounding?.target_depth ?? null,
        realisedDepth: line.rounding?.realised_depth ?? null,
        netBenefit: null, currencyCode: line.currency_code,
        createdAt: line.created_at ?? null, executedUnits: null, remainingUnits: null, trancheWeek: null,
        provenance: line.provenance,
      });
    }
  }
  for (const group of pack.exit_lanes ?? []) {
    for (const line of group.lines) {
      lines.push({
        subject: 'exit_consolidation_line', subjectId: line.line_id, commandId: line.command_id,
        state: line.state, stateObservedAt: line.state_observed_at,
        from: group.source_store_key, fromAbsentReason: null, to: group.destination_store_key,
        // The exit lane moves an option, not an article: the screens' row header shows it, and the
        // file puts it in `option_id` and leaves `article_key` blank (see `optionId`).
        articleKey: line.option_id, optionId: line.option_id, size: sizeName(line),
        units: line.units, authorisedUnits: line.units, adjustedUnits: null,
        fromPrice: null, toPrice: null, shelfPrice: null, targetDepth: null, realisedDepth: null, netBenefit: line.net_benefit, currencyCode: line.currency_code,
        createdAt: line.created_at, executedUnits: line.executed_units ?? null,
        remainingUnits: line.remaining_units ?? null,
        trancheWeek: line.tranche_week === undefined || line.tranche_week === '' ? null : line.tranche_week,
        provenance: line.provenance,
      });
    }
  }
  return lines;
}

/**
 * The two states a planner may report, and the states each is reported FROM (ADR 6037). Since
 * ADR 6105 "confirmed executed" also closes a partly executed command: the rest was executed.
 */
export const REPORT_FROM: Readonly<Record<'dispatched' | 'acknowledged', readonly string[]>> = {
  dispatched: ['approved'],
  acknowledged: ['dispatched', 'partially_executed'],
};
export type PlannerReport = keyof typeof REPORT_FROM;

/** The commands a report applies to: only those one step behind it. Anything else is refused. */
export function reportable(lines: readonly PackLine[], report: PlannerReport): readonly string[] {
  return lines.filter((line) => REPORT_FROM[report].includes(line.state)).map((line) => line.commandId);
}

export interface PackTally {
  /** Approved, not yet exported. */
  readonly toExport: number;
  /** Exported, awaiting a planner's confirmation. NOT executed. */
  readonly awaitingConfirmation: number;
  /** A planner confirmed it was executed in Oracle (`acknowledged`). */
  readonly confirmed: number;
  /**
   * The target reported it executed (`executed`). Owner ruling R-16 (bata-ui#166 W4-8):
   * counted separately from `confirmed`, never added to it.
   */
  readonly executed: number;
  /** Part of it confirmed executed; the rest still to execute (ADR 6105). NOT done. */
  readonly partiallyExecuted: number;
  /** Withdrawn by a planner with a governed reason (ADR 6105). */
  readonly cancelled: number;
  /** Any other state, counted as itself and never folded into the ones above. */
  readonly other: number;
}

export function packTally(lines: readonly PackLine[]): PackTally {
  let toExport = 0;
  let awaitingConfirmation = 0;
  let confirmed = 0;
  let executed = 0;
  let partiallyExecuted = 0;
  let cancelled = 0;
  let other = 0;
  for (const line of lines) {
    if (line.state === 'approved') toExport += 1;
    else if (line.state === 'dispatched') awaitingConfirmation += 1;
    else if (line.state === 'acknowledged') confirmed += 1;
    else if (line.state === 'executed') executed += 1;
    else if (line.state === 'partially_executed') partiallyExecuted += 1;
    else if (line.state === 'cancelled') cancelled += 1;
    else other += 1;
  }
  return { toExport, awaitingConfirmation, confirmed, executed, partiallyExecuted, cancelled, other };
}

// ── the two CSV sheets ───────────────────────────────────────────────────────

const PLATFORM_VERSION = buildVersion();

const text = (value: string): ExportCell => ({ kind: 'text', value });
const notApplicable: ExportCell = { kind: 'missing', state: 'not-ranged' };
const notReported: ExportCell = { kind: 'missing', state: 'not-reported' };
const numberOr = (value: number | null, absent: ExportCell = notApplicable): ExportCell => (
  value === null ? absent : { kind: 'number', raw: value }
);

function perimeterFilters(pack: PackPerimeter): { field: string; operator: '='; value: string }[] {
  return [
    ...(pack.run_id === undefined
      ? [{ field: 'country_key', operator: '=' as const, value: pack.country_key }]
      : [{ field: 'run_id', operator: '=' as const, value: pack.run_id }]),
    ...(pack.state === null ? [] : [{ field: 'state', operator: '=' as const, value: pack.state }]),
  ];
}

function packProvenance(pack: PackPerimeter): Provenance {
  return {
    filters: perimeterFilters(pack),
    hierarchyNode: pack.country_key,
    timeWindow: NOT_PUBLISHED,
    scenario: NOT_PUBLISHED,
    planVersionId: NOT_PUBLISHED,
    measureDefinitions: [
      {
        measure: 'write_back_state',
        definition:
          'The command state as the platform holds it, verbatim. approved: decided, not yet exported. dispatched: a pack containing it was exported; it is NOT executed. partially_executed: a planner confirmed part of its units executed; the rest is still to execute. acknowledged: a planner confirmed it was executed in Oracle. cancelled: a planner withdrew it with a governed reason. No other state is reported by a planner.',
        registerVersion: '',
      },
      {
        measure: 'provenance',
        definition:
          'Per line, the decision that authorised it: the run, the decision identifier (the Idempotency-Key of the planner act), who decided, when, and the governed reason code.',
        registerVersion: '',
      },
    ],
    generatedAt: new Date().toISOString(),
    generatedBy: NO_PRINCIPAL,
    platformVersion: PLATFORM_VERSION,
  };
}

// ── names beside codes (usability audit B3) ─────────────────────────────────

/**
 * A line's names, from ITS run's reports (`run-names.tsx`) — `null` where the role names no store
 * or article the reports could name: a proposal's origin is a supply node, a price step's «from» is
 * its article and its «to» a week, and an exit lane belongs to a consolidation run, not to an
 * in-season run's reports (B8). Verified against bata-platform v0.61.0
 * `services/repositories/execution.py`: a pack line's `proposal_id` / `transfer_id` is the command's
 * `subject_id` joined to the run's own proposal / transfer, so its store and article keys are the
 * keys the run's reports name. The join is by key, never by a guessed id.
 */
export interface PackNames {
  readonly article: Named | null;
  readonly from: Named | null;
  readonly to: Named | null;
}

export function packLineNames(line: PackLine, names: RunNames): PackNames {
  switch (line.subject) {
    case 'proposal':
      return { article: names.article(line.articleKey), from: null, to: names.store(line.to) };
    case 'transfer':
      return { article: names.article(line.articleKey), from: line.from === null ? null : names.store(line.from), to: names.store(line.to) };
    case 'price_action':
      return { article: names.article(line.articleKey), from: null, to: null };
    default:
      return { article: null, from: null, to: null };
  }
}

const PACK_NAME_COLUMNS: readonly ExportColumn[] = [
  { key: 'article_name', headerKey: 'inseason.reportAttributes.column.articleName', kind: 'text', descriptionKey: 'inseason.columnGuide.reportAttributes.articleName' },
  { key: 'group_from_name', headerKey: 'inseason.executionPack.column.groupFromName', kind: 'text', descriptionKey: 'inseason.columnGuide.executionPack.groupFromName' },
  { key: 'group_to_name', headerKey: 'inseason.executionPack.column.groupToName', kind: 'text', descriptionKey: 'inseason.columnGuide.executionPack.groupToName' },
  ...['article_name', 'group_from_name', 'group_to_name'].map((column): ExportColumn => ({
    key: `${column}_absent_reason`,
    headerKey: 'inseason.reportAttributes.column.absentReason',
    headerValues: { column },
    kind: 'text',
    descriptionKey: 'inseason.columnGuide.reportAttributes.absent_reason',
  })),
];

/** A line's name cells: each name (not applicable where the role names none), then each reason. */
function packNameCells(named: PackNames): ExportCell[] {
  const roles = [named.article, named.from, named.to];
  return [
    ...roles.map((one): ExportCell => (one === null ? notApplicable : one.name === null ? notReported : text(one.name))),
    ...roles.map((one): ExportCell => text(one === null || one.name !== null ? '' : one.reason ?? '')),
  ];
}

/** The lines sheet's columns, each header key spelled out so the catalogue check sees it. */
const PACK_COLUMNS: readonly ExportColumn[] = [
  { key: 'subject_kind', headerKey: 'inseason.executionPack.column.subjectKind', kind: 'label', descriptionKey: 'inseason.columnGuide.executionPack.subjectKind' },
  { key: 'command_id', headerKey: 'inseason.executionPack.column.commandId', kind: 'text', descriptionKey: 'inseason.columnGuide.executionPack.commandId' },
  { key: 'state', headerKey: 'inseason.executionPack.column.state', kind: 'label', descriptionKey: 'inseason.columnGuide.executionPack.state' },
  { key: 'state_observed_at', headerKey: 'inseason.executionPack.column.stateObservedAt', kind: 'date', descriptionKey: 'inseason.columnGuide.executionPack.stateObservedAt' },
  { key: 'group_from', headerKey: 'inseason.executionPack.column.groupFrom', kind: 'text', descriptionKey: 'inseason.columnGuide.executionPack.groupFrom' },
  { key: 'group_to', headerKey: 'inseason.executionPack.column.groupTo', kind: 'text', descriptionKey: 'inseason.columnGuide.executionPack.groupTo' },
  { key: 'article_key', headerKey: 'inseason.executionPack.column.articleKey', kind: 'text', descriptionKey: 'inseason.columnGuide.executionPack.articleKey' },
  { key: 'option_id', headerKey: 'inseason.executionPack.column.optionId', kind: 'text', descriptionKey: 'inseason.columnGuide.executionPack.optionId' },
  // The size as the platform named it — key (an id), code, scale, run position, absent reason
  // (bata-platform#470 §1, `docs/adr/9027`) — in place of the key alone.
  ...SIZE_EXPORT_COLUMNS,
  { key: 'units', headerKey: 'inseason.executionPack.column.units', kind: 'number', descriptionKey: 'inseason.columnGuide.executionPack.units' },
  { key: 'authorised_units', headerKey: 'inseason.executionPack.column.authorisedUnits', kind: 'number', descriptionKey: 'inseason.columnGuide.executionPack.authorisedUnits' },
  { key: 'adjusted_units', headerKey: 'inseason.executionPack.column.adjustedUnits', kind: 'number', descriptionKey: 'inseason.columnGuide.executionPack.adjustedUnits' },
  { key: 'from_price', headerKey: 'inseason.executionPack.column.fromPrice', kind: 'number', descriptionKey: 'inseason.columnGuide.executionPack.fromPrice' },
  { key: 'to_price', headerKey: 'inseason.executionPack.column.toPrice', kind: 'number', descriptionKey: 'inseason.columnGuide.executionPack.toPrice' },
  // bata-reqs issue 38 R4 (platform ADR 6190): the shelf price to set, and the target and realised %.
  { key: 'shelf_price', headerKey: 'inseason.executionPack.column.shelfPrice', kind: 'number', descriptionKey: 'inseason.columnGuide.executionPack.shelfPrice' },
  { key: 'target_depth', headerKey: 'inseason.executionPack.column.targetDepth', kind: 'number', descriptionKey: 'inseason.columnGuide.executionPack.targetDepth' },
  { key: 'realised_depth', headerKey: 'inseason.executionPack.column.realisedDepth', kind: 'number', descriptionKey: 'inseason.columnGuide.executionPack.realisedDepth' },
  { key: 'net_benefit', headerKey: 'inseason.executionPack.column.netBenefit', kind: 'number', descriptionKey: 'inseason.columnGuide.executionPack.netBenefit' },
  { key: 'currency_code', headerKey: 'inseason.executionPack.column.currencyCode', kind: 'text', descriptionKey: 'inseason.columnGuide.executionPack.currencyCode' },
  { key: 'executed_units', headerKey: 'inseason.executionPack.column.executedUnits', kind: 'number', descriptionKey: 'inseason.columnGuide.executionPack.executedUnits' },
  { key: 'remaining_units', headerKey: 'inseason.executionPack.column.remainingUnits', kind: 'number', descriptionKey: 'inseason.columnGuide.executionPack.remainingUnits' },
  // bata-reqs ADR 9000 (platform #891): the week an exit lane ships in under the units-sent cap.
  { key: 'tranche_week', headerKey: 'inseason.executionPack.column.trancheWeek', kind: 'date', descriptionKey: 'inseason.columnGuide.executionPack.trancheWeek' },
  { key: 'created_at', headerKey: 'inseason.executionPack.column.createdAt', kind: 'date', descriptionKey: 'inseason.columnGuide.executionPack.createdAt' },
  { key: 'subject_id', headerKey: 'inseason.executionPack.column.subjectId', kind: 'text', descriptionKey: 'inseason.columnGuide.executionPack.subjectId' },
  { key: 'run_id', headerKey: 'inseason.executionPack.column.runId', kind: 'text', descriptionKey: 'inseason.columnGuide.executionPack.runId' },
  { key: 'decision_id', headerKey: 'inseason.executionPack.column.decisionId', kind: 'text', descriptionKey: 'inseason.columnGuide.executionPack.decisionId' },
  // Who decided, by name (usability audit B6); the principal id beside it, technical.
  { key: 'actor_name', headerKey: 'inseason.executionPack.column.actor', kind: 'label', display: PERSON_NAME_DISPLAY, descriptionKey: 'inseason.columnGuide.executionPack.actorName' },
  { key: 'actor', headerKey: 'inseason.executionPack.column.actorId', kind: 'text', technical: true, descriptionKey: 'inseason.columnGuide.executionPack.actorId' },
  { key: 'decided_at', headerKey: 'inseason.executionPack.column.decidedAt', descriptionKey: 'inseason.columnGuide.executionPack.decidedAt', kind: 'date' },
  { key: 'reason_code', headerKey: 'inseason.executionPack.column.reasonCode', descriptionKey: 'inseason.columnGuide.executionPack.reasonCode', kind: 'label' },
];

/**
 * The lines sheet: one row per command, its group, its quantities and its authority — and, given
 * the run's names (the per-run pack only; a market's pack spans runs), each line's article, origin
 * and destination by name beside the codes, as the panel shows them.
 */
export function packLinesRequest(pack: PackPerimeter, lines: readonly PackLine[], names: RunNames | null = null): ExportRequest {
  const columns: readonly ExportColumn[] = names === null ? PACK_COLUMNS : [
    ...PACK_COLUMNS.slice(0, PACK_COLUMNS.findIndex((column) => column.key === 'option_id') + 1),
    ...PACK_NAME_COLUMNS,
    ...PACK_COLUMNS.slice(PACK_COLUMNS.findIndex((column) => column.key === 'option_id') + 1),
  ];
  const rows: ExportCell[][] = lines.map((line) => [
    { kind: 'label', value: line.subject, code: line.subject },
    text(line.commandId),
    { kind: 'label', value: line.state, code: line.state },
    { kind: 'date', iso: line.stateObservedAt },
    // The proposal origin is published absent (`origin_not_recorded`): not reported, not blank.
    line.from === null ? notReported : text(line.from),
    text(line.to),
    // An exit lane moves an option, not an article: its key is in `option_id`, this cell blank.
    text(line.optionId === null ? line.articleKey : ''),
    text(line.optionId ?? ''),
    ...(names === null ? [] : packNameCells(packLineNames(line, names))),
    // A price step has no size: each of the five size cells is not applicable.
    ...(line.size === null ? SIZE_EXPORT_COLUMNS.map(() => notApplicable) : sizeExportCells(line.size)),
    numberOr(line.units),
    numberOr(line.authorisedUnits, line.subject === 'proposal' ? notReported : notApplicable),
    numberOr(line.adjustedUnits, line.subject === 'proposal' ? notReported : notApplicable),
    numberOr(line.fromPrice),
    numberOr(line.toPrice),
    numberOr(line.shelfPrice, line.subject === 'price_action' ? notReported : notApplicable),
    numberOr(line.targetDepth, line.subject === 'price_action' ? notReported : notApplicable),
    numberOr(line.realisedDepth, line.subject === 'price_action' ? notReported : notApplicable),
    numberOr(line.netBenefit),
    line.currencyCode === null ? notApplicable : text(line.currencyCode),
    numberOr(line.executedUnits, line.units === null ? notApplicable : notReported),
    numberOr(line.remainingUnits, line.units === null ? notApplicable : notReported),
    // An exit lane with no tranche ships as before, and no other line has one: an empty cell either way.
    line.trancheWeek === null ? text('') : { kind: 'date', iso: line.trancheWeek },
    line.createdAt === null ? notReported : { kind: 'date', iso: line.createdAt },
    text(line.subjectId),
    text(line.provenance.run_id),
    text(line.provenance.decision_id),
    personExportCell(line.provenance.actor, line.provenance.actor_name, line.provenance.actor_name_absent_reason),
    text(line.provenance.actor),
    { kind: 'date', iso: line.provenance.decided_at },
    { kind: 'label', value: line.provenance.reason_code, code: line.provenance.reason_code },
  ]);
  return {
    id: 'inseason.executionPack.lines',
    titleKey: 'inseason.executionPack.title',
    columns,
    rows: () => rows,
    rowCount: rows.length,
    provenance: packProvenance(pack),
    perimeter: {
      filters: perimeterFilters(pack),
      sort: [],
      visibleColumns: columns.map((column) => column.key),
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The provenance sheet: the export's nine G2.4 fields as `field,value` rows, translated.
 *
 * CSV has one sheet per file (ADR 9010 retired the workbook), so the provenance that an XLSX
 * would have carried on its second sheet leaves as a second file beside the lines — downloaded
 * in the same act, named after the same export.
 */
export function packProvenanceRequest(
  lines: ExportRequest,
  translate: (key: string) => string,
): ExportRequest {
  const rows: ExportCell[][] = provenanceRows(lines.provenance).map((row) => [
    text(translate(row.labelKey)),
    text(row.valueIsKey === true ? translate(row.value) : row.value),
  ]);
  return {
    id: 'inseason.executionPack.provenance',
    titleKey: 'inseason.executionPack.provenanceTitle',
    columns: [
      { key: 'field', headerKey: 'inseason.executionPack.column.field', kind: 'text' },
      { key: 'value', headerKey: 'inseason.executionPack.column.value', kind: 'text' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: lines.provenance,
    perimeter: lines.perimeter,
  };
}
